/**
 * Broadcast super-titles (HUD Kit E, B4PH3): TOUCHDOWN!!, WHAT A FAKE!, PICKED OFF!, SACKED!, OFFSIDE,
 * FIELD GOAL IS GOOD!… A 'big' title is a full-width tilted band with hype rays in the tone colour, the head
 * (expression art) with its fist glove, a huge stroked title that slams in, a sub-line pill and an optional
 * corner stamp (+6). A 'pop' is a smaller comic callout (TRUCKED!, HURDLED!, HAIL MARY!).
 * Entrances/exits are pure CSS keyed on the title id; `--dur` drives the exit timing.
 */
import './super-title.css';
import type { CharacterId } from '@hf/shared';
import { isCharacterId } from '@hf/shared';
import { Glove, Head, Stroke } from '../components';
import { ui } from '@hf/client-core/ui-model/store';

/** Long titles ("FIELD GOAL IS GOOD!") step down a size so they always fit the band. */
export const lengthClass = (text: string): string =>
  text.length > 15 ? 'len-xl' : text.length > 9 ? 'len-l' : '';

export function SuperTitleLayer() {
  const t = ui.superTitle.value;
  // An instant replay owns the screen (online the live play may announce things underneath it).
  if (!t || ui.replay.value) return null;
  // Football 101's lesson-clear / YOU MADE THE TEAM cards are modal: a play's banner still fading out (TURNOVER ON
  // DOWNS) was drawn right across "FOOTBALL 101 COMPLETE" (QA r4). The card cuts it.
  const tutPhase = ui.tutorial.value?.phase;
  if (tutPhase === 'clear' || tutPhase === 'complete') return null;
  const size = t.size ?? 'big';
  const style = { '--dur': `${t.durationMs}ms` } as Record<string, string>;
  const ch: CharacterId | null = t.character && isCharacterId(t.character) ? t.character : null;
  const away = t.side === 1;
  if (size === 'pop')
    return (
      <div class={`hf-pop tone-${t.tone}`} key={t.id} style={style} role="status" aria-live="assertive">
        <div class="hf-pop__burst" aria-hidden="true" />
        <Stroke text={t.text} class="t-display hf-pop__text" />
        {t.sub ? <div class="hf-pop__sub t-broadcast">{t.sub}</div> : null}
        {t.stamp ? <div class="hf-pop__stamp t-display">{t.stamp}</div> : null}
      </div>
    );
  return (
    <div
      class={`hf-super tone-${t.tone} ${ch ? 'has-head' : ''} ${away ? 'away' : 'home'}`}
      key={t.id}
      style={style}
      role="status"
      aria-live="assertive"
    >
      <div class="hf-super__band">
        <div class="hf-super__rays" aria-hidden="true" />
        <div class="hf-super__shine" aria-hidden="true" />
        {ch ? (
          <div class="hf-super__rig" aria-hidden="true">
            <Head
              character={ch}
              expression={t.expression ?? 'celebrating'}
              flip={away}
              class="hf-super__head"
            />
            <Glove side={away ? 'away' : 'home'} pose="fist" flip={away} class="hf-super__glove" />
          </div>
        ) : null}
        <div class="hf-super__copy">
          <Stroke text={t.text} class={`t-display hf-super__text ${lengthClass(t.text)}`} />
          {t.sub ? <div class="hf-super__sub t-broadcast">{t.sub}</div> : null}
        </div>
      </div>
      {t.stamp ? <div class="hf-super__stamp t-display">{t.stamp}</div> : null}
      <div class="hf-super__confetti" aria-hidden="true">
        {Array.from({ length: 14 }, (_, i) => (
          <i key={i} style={{ '--i': i } as Record<string, number>} />
        ))}
      </div>
    </div>
  );
}
